import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'

import { AGENT_URL } from '../config/env'

export interface PromptVersion {
  id: string
  content: string
  note: string
  parent_id: string | null
  created_at: string
  created_by: string | null
}

/** A history row. Carries no text — see `fetchVersion`. */
export interface PromptVersionSummary {
  id: string
  note: string
  parent_id: string | null
  created_at: string
  created_by: string | null
}

export interface PromptVersionsResponse {
  live: PromptVersion | null
  versions: PromptVersionSummary[]
}

export interface PromptVersionsState<T extends PromptVersionsResponse> {
  data: T | undefined
  live: PromptVersion | null
  versions: PromptVersionSummary[]
  isLoading: boolean
  loadError: Error | null
  /**
   * `parentId` is the version the text being saved was derived from, which is
   * not always the newest one this hook has seen: a refetch can advance `live`
   * while an edit is open. Sending the newest would let the server's conflict
   * check pass against a version the author never saw, quietly replacing it.
   */
  save: (
    content: string,
    note: string,
    parentId: string | null
  ) => Promise<void>
  isSaving: boolean
  fetchVersion: (versionId: string) => Promise<PromptVersion>
}

/**
 * The body as JSON, or a readable failure when it is not JSON at all.
 *
 * An unknown /api path falls through to the SPA catch-all, which answers 200
 * with index.html. Parsing that raises "Unexpected token '<'", which says
 * nothing about the server missing the route.
 */
export async function readJson(response: Response, routes: string) {
  const body = await response.text()
  try {
    return JSON.parse(body)
  } catch {
    throw new Error(
      body.trimStart().startsWith('<')
        ? `The API answered with a page instead of data — it may be running a build without the ${routes} routes.`
        : 'The API answered with something that is not JSON.'
    )
  }
}

export async function readError(response: Response, fallback: string) {
  try {
    const body = await response.json()
    return body.error || fallback
  } catch {
    return fallback
  }
}

/** What distinguishes one family of versioned prompts from another. */
export interface PromptResource {
  /** Path under the agent API, e.g. `admin/agent-prompt`. */
  path: string
  /** What these routes are called, for a message naming a build that lacks them. */
  routes: string
  /** React Query key prefix. */
  queryKey: string
  /**
   * The listing that summarises these, when one exists.
   *
   * A save changes what the list reports — the size, who saved it, when — so
   * it has to be refetched too, or the row keeps describing the version it
   * replaced while the editor beside it shows the new one.
   */
  listQueryKey?: string
  /** What one of these is called, for messages a person reads. */
  noun: string
}

/**
 * Serves one versioned prompt and everything it has been.
 *
 * Pass `enabled: false` to hold the read until the caller needs it.
 *
 * Saving appends a version and makes it live, so the newest entry is what is
 * in force and every earlier one stays available to restore.
 */
export function usePromptVersions<T extends PromptVersionsResponse>(
  resource: PromptResource,
  name: string,
  enabled: boolean = true
): PromptVersionsState<T> {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const { path, routes, queryKey, listQueryKey, noun } = resource

  const authorized = useCallback(async () => {
    const token = await getToken()
    if (!token) {
      throw new Error('No authentication token available')
    }
    return token
  }, [getToken])

  const query = useQuery({
    queryKey: [queryKey, name],
    queryFn: async (): Promise<T> => {
      const token = await authorized()
      const response = await fetch(`${AGENT_URL}/${path}/${name}`, {
        headers: { Authorization: token },
      })
      if (!response.ok) {
        throw new Error(
          await readError(
            response,
            `Failed to load the ${noun}: ${response.status}`
          )
        )
      }
      return readJson(response, routes)
    },
    // Every version carries its whole body, and these run to tens of
    // kilobytes, so opening the config page for an unrelated setting would
    // otherwise pull far more than anybody asked to see.
    enabled,
  })

  const mutation = useMutation({
    mutationFn: async ({
      content,
      note,
      parentId,
    }: {
      content: string
      note: string
      parentId: string | null
    }): Promise<PromptVersion> => {
      const token = await authorized()
      // The row this text was derived from, decided by the caller. Reading
      // the newest one here instead would send a parent the author never saw
      // whenever a refetch landed mid-edit, and the server would accept it.
      const response = await fetch(`${AGENT_URL}/${path}/${name}`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, note, parent_id: parentId }),
      })
      if (!response.ok) {
        // A 409 carries the reason someone else's save is in the way, and it
        // is the one message the author has to read rather than retry past.
        throw new Error(
          await readError(
            response,
            `Failed to save the ${noun}: ${response.status}`
          )
        )
      }
      return (await readJson(response, routes)).version
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey, name] })
      if (listQueryKey) {
        queryClient.invalidateQueries({ queryKey: [listQueryKey] })
      }
    },
  })

  const save = useCallback(
    async (content: string, note: string, parentId: string | null) => {
      await mutation.mutateAsync({ content, note, parentId })
    },
    [mutation]
  )

  const fetchVersion = useCallback(
    async (versionId: string): Promise<PromptVersion> => {
      const token = await authorized()
      const response = await fetch(
        `${AGENT_URL}/${path}/${name}/versions/${versionId}`,
        { headers: { Authorization: token } }
      )
      if (!response.ok) {
        throw new Error(
          await readError(
            response,
            `Failed to load the version: ${response.status}`
          )
        )
      }
      return (await readJson(response, routes)).version
    },
    [authorized, name, path, routes]
  )

  return {
    data: query.data,
    live: query.data?.live ?? null,
    versions: query.data?.versions ?? [],
    // `isPending`, not `isLoading`: a query that has not been enabled yet is
    // pending without fetching, and the editor must not mount on an empty
    // baseline and then remount when the real text lands — the outgoing
    // editor would flush the empty text back over the draft.
    isLoading: query.isPending,
    loadError: query.error,
    save,
    isSaving: mutation.isPending,
    fetchVersion,
  }
}
